import React, { useEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist/legacy/build/pdf';
import { URL } from '../../../variables';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/vendor/pdf.worker.min.js`;

export default function TrainingPdf({ content, trainingId, token, onProgress }) {
    const [document, setDocument] = useState(null);
    const [page, setPage] = useState(1);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [readPages, setReadPages] = useState(content.watchtime?.pdf_pages || []);
    const pages = useRef(content.watchtime?.pdf_pages || []);
    const canvas = useRef(null), viewport = useRef(null), ready = useRef(false);
    const callback = useRef(onProgress);
    callback.current = onProgress;
    useEffect(() => {
        let active = true;
        const task = pdfjs.getDocument({
            url: `${URL}api/fiscal/training_pdf/${trainingId}/${content.modulo_id}/${content.id}`,
            httpHeaders: { Authorization: `Bearer ${token}` },
            disableRange: true, disableStream: true,
        });
        task.promise.then(pdf => { if (active) setDocument(pdf); }).catch(() => { if (active) { setError('Não foi possível carregar o PDF. Atualize a página para tentar novamente.'); setLoading(false); } });
        return () => { active = false; task.destroy(); };
    }, [content.file, content.id, content.modulo_id, trainingId, token]);
    useEffect(() => {
        if (!document) return;
        let active = true, render;
        ready.current = false; setLoading(true);
        document.getPage(page).then(pdfPage => {
            if (!active || !canvas.current) return;
            const original = pdfPage.getViewport({ scale: 1 });
            const width = Math.max(240, viewport.current.clientWidth - 32);
            const scale = width / original.width;
            const view = pdfPage.getViewport({ scale });
            const ratio = window.devicePixelRatio || 1;
            canvas.current.width = Math.floor(view.width * ratio);
            canvas.current.height = Math.floor(view.height * ratio);
            canvas.current.style.width = `${view.width}px`;
            canvas.current.style.height = `${view.height}px`;
            render = pdfPage.render({ canvasContext: canvas.current.getContext('2d'), viewport: view, transform: ratio === 1 ? null : [ratio, 0, 0, ratio, 0, 0] });
            return render.promise;
        }).then(() => {
            if (!active) return;
            ready.current = true; setLoading(false); viewport.current.scrollTop = 0;
            requestAnimationFrame(() => { if (active) checkBottom(); });
        }).catch(e => { if (active && e.name !== 'RenderingCancelledException') { setError('Erro ao exibir a página do PDF.'); setLoading(false); } });
        return () => { active = false; if (render) render.cancel(); };
    }, [document, page]);
    const checkBottom = () => {
        const area = viewport.current;
        if (!ready.current || !document || area.scrollTop + area.clientHeight < area.scrollHeight - 8 || pages.current.includes(page)) return;
        pages.current = [...pages.current, page]; setReadPages(pages.current);
        callback.current({ pdf_page_count: document.numPages, pdf_pages: pages.current, watch_time: 0 });
    };
    return <div className="training-pdf">
        <div className="training-pdf-toolbar">
            <button type="button" className="btn btn-outline-secondary" disabled={page === 1 || loading} onClick={() => setPage(page - 1)}>Anterior</button>
            <strong>Página {page} de {document?.numPages || '…'}</strong>
            <button type="button" className="btn btn-primary" disabled={!document || page >= document.numPages || loading || !readPages.includes(page)} onClick={() => setPage(page + 1)}>Próxima</button>
        </div>
        {error && <p className="alert alert-danger" role="alert">{error}</p>}
        <div ref={viewport} className="training-pdf-pages" onScroll={checkBottom} aria-label={`Página ${page} do PDF`}>
            {loading && <p role="status">Carregando página…</p>}
            <canvas ref={canvas} role="img" aria-label={`Conteúdo da página ${page}`} />
        </div>
        <p className="training-hint">Leia cada página até o final para liberar a próxima. A conclusão é liberada após a última página.</p>
    </div>;
}
